import React, {useEffect, useState} from 'react';
import {useProjectData} from "../../hooks/useProjectData";
import {
    Box,
    CircularProgress,
    Grid,
    Stack,
    Tab,
    Tabs,
    Typography,
} from "@mui/material";
import ProjectPageHeader from "../layouts/ProjectPageHeader";
import {ProjectRequests} from "../../api/requests/ProjectRequests";
import {RowRequests} from "../../api/requests/RowRequests";
import {useNavigate, useSearchParams} from "react-router-dom";
import ConfirmDeleteModal from "../modal/ConfirmDeleteModal";
import {useToasts} from "../../hooks/useToasts"
import DangerActionBox from "../form/DangerActionBox"
import DangerZone from "../form/DangerZone"
import ApiError from "../../api/ApiError"
import ErrorMessage from "../error/ErrorMessage"
import AsyncButton from "../button/AsyncButton"
import QualityCheckSettingsForm from "./QualityCheckSettingsForm"
import ProjectSettingsForm from "./ProjectSettingsForm"
import {ProjectJson} from "../../api/json/ProjectJson"
import DatasetAllocationInput from "../rows/DatasetAllocationInput"
import {RoleCheck} from "../../library/RoleCheck"
import ColumnsTab from "./ColumnsTab"
import SynthesisSettingsTab from "./SynthesisSettingsTab"
import ProjectApiTab from "./ProjectApiTab"
import ExportButton from "../layouts/Nav/ExportButton"
import {useBreadcrumbs} from "../layouts/Nav/BreadcrumbsProvider"
import {NAV_LABELS} from "../../library/NavLabels"

type SettingsTab = "general" | "columns" | "dataset" | "evaluation" | "synthesis" | "api" | "advanced"

const SETTINGS_TABS: SettingsTab[] = ["general", "columns", "dataset", "evaluation", "synthesis", "api", "advanced"]

function SettingsPage() {
    useBreadcrumbs([{label: NAV_LABELS.SETTINGS}])
    const {project, setProject} = useProjectData()

    const [searchParams, setSearchParams] = useSearchParams()
    const tabParam = searchParams.get("tab") as SettingsTab | null
    const activeTab: SettingsTab = tabParam && SETTINGS_TABS.includes(tabParam) ? tabParam : "general"

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [deleteError, setDeleteError] = useState<ApiError>()

    const savedValidationPercent = project?.stats.percentValidationRows ?? 0
    const [validationPercent, setValidationPercent] = useState<number>(savedValidationPercent)
    const [allocateSaving, setAllocateSaving] = useState(false)
    const [allocateError, setAllocateError] = useState<ApiError>()

    // Re-sync local edit state once the project loads (or when its saved value changes
    // after an external update). useState's initializer only runs once, so without this
    // the form would stay stuck at 0 if it mounts before the project finishes loading.
    useEffect(() => {
        setValidationPercent(savedValidationPercent)
    }, [savedValidationPercent])

    const navigate = useNavigate()
    const toasts = useToasts()

    const handleUpdatedProject = (updated: ProjectJson) => {
        setProject(updated)
        toasts.showSaved()
    }

    const handleDelete = () => {
        if (!project) return

        ProjectRequests.delete(project.id)
            .then(() => navigate("/"))
            .catch(setDeleteError)
    }

    const handleSaveAllocation = () => {
        if (!project) return
        setAllocateSaving(true)
        RowRequests.updateAll(project.id, validationPercent)
            .then(() => {
                setAllocateError(undefined)
                toasts.showSaved()
            })
            .catch(setAllocateError)
            .finally(() => setAllocateSaving(false))
    }

    if (!project) {
        return <CircularProgress/>
    }

    const isAdmin = RoleCheck.isAdmin()
    const hasRows = project.stats.totalRows > 0
    const hasTrainingRows = project.stats.numberOfTrainingRows > 0

    return (
        <>
            <ProjectPageHeader pageTitle="Settings"/>

            <Box sx={{borderBottom: 1, borderColor: 'divider', mb: 3}}>
                <Tabs value={activeTab} onChange={(_, value) => {
                    const next = value as SettingsTab
                    setSearchParams(next === "general" ? {} : {tab: next}, {replace: true})
                }}>
                    <Tab label="General" value="general"/>
                    <Tab label="Columns" value="columns"/>
                    <Tab label="Dataset" value="dataset"/>
                    <Tab label="Quality Checks" value="evaluation"/>
                    <Tab label="Synthesis" value="synthesis"/>
                    <Tab label="API" value="api"/>
                    <Tab label="Advanced" value="advanced"/>
                </Tabs>
            </Box>

            {activeTab === "general" &&
                <Grid container spacing={4} justifyContent="space-between">
                    <Grid item md={6} sm={12}>
                        <ProjectSettingsForm project={project}
                                             didUpdate={handleUpdatedProject}/>
                    </Grid>
                </Grid>
            }

            {activeTab === "advanced" &&
                <Grid container spacing={4} justifyContent="space-between">
                    <Grid item md={6} sm={12}>
                        <DangerZone>
                            <DangerActionBox title="Delete this project"
                                             description="All your rows will be deleted."
                                             buttonLabel="Delete" onAction={() => setDeleteConfirmOpen(true)}
                                             actionPending={deleteConfirmOpen}/>
                        </DangerZone>
                    </Grid>
                </Grid>
            }

            {activeTab === "columns" &&
                <ColumnsTab project={project}/>
            }

            {activeTab === "evaluation" &&
                <Grid container spacing={4} justifyContent="space-between">
                    <Grid item md={6} sm={12}>
                        <QualityCheckSettingsForm project={project}
                                          didUpdate={handleUpdatedProject}/>
                    </Grid>
                </Grid>
            }

            {activeTab === "synthesis" &&
                <SynthesisSettingsTab project={project}/>
            }

            {activeTab === "api" &&
                <ProjectApiTab project={project} didUpdate={handleUpdatedProject}/>
            }

            {activeTab === "dataset" &&
                <Stack spacing={4} sx={{maxWidth: 520}}>
                    {isAdmin && hasRows &&
                        <Stack spacing={2} alignItems="flex-start">
                            <Typography variant="subtitle1">Allocate Training/Validation</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Adjust the percentage of rows reserved for evaluating model
                                performance on unseen data.
                            </Typography>
                            <DatasetAllocationInput
                                currentNumberOfTrainingExamples={project.stats.numberOfTrainingRows}
                                currentNumberOfValidationExamples={project.stats.numberOfValidationRows}
                                validationPercent={validationPercent}
                                onChange={setValidationPercent}
                            />
                            <AsyncButton waiting={allocateSaving}
                                         disabled={validationPercent === savedValidationPercent}
                                         onClick={handleSaveAllocation}>
                                Save
                            </AsyncButton>
                            {allocateError && <ErrorMessage error={allocateError}/>}
                        </Stack>
                    }

                    {hasTrainingRows &&
                        <Stack spacing={1} alignItems="flex-start">
                            <Typography variant="subtitle1">Export</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Download this project's rows as a CSV or JSONL file.
                            </Typography>
                            <Box sx={{mt: 1}}>
                                <ExportButton project={project}/>
                            </Box>
                        </Stack>
                    }

                    {!hasRows && !hasTrainingRows &&
                        <Typography variant="body2" color="text.secondary">
                            Add some rows to this project to enable dataset actions.
                        </Typography>
                    }
                </Stack>
            }

            <ConfirmDeleteModal heading={`Delete "${project.name}" project`}
                                body="This will permanently delete all data associated with the project immediately and cannot be reversed."
                                open={deleteConfirmOpen}
                                setOpen={setDeleteConfirmOpen}
                                confirmedDelete={handleDelete}
                                error={deleteError}
            />
        </>
    );
}

export default SettingsPage;
